/**
 * Tiny module-level flag used to coordinate between the floating avatar's
 * pointer/drag gesture (in `useWebAvatarDrag`) and any sibling tap-capture
 * elements that share screen real estate with the avatar (notably the
 * tap-to-play/pause `Pressable` inside `MediaPlayerShell`).
 *
 * Why a module-level flag instead of context/state?
 *   - The handler that needs to bail (the `Pressable`'s `onPress`) is fired
 *     from a synthetic click event. We need to make a synchronous decision
 *     inside that callback, so a plain mutable flag is the cleanest tool.
 *   - The avatar gesture and the tap-capture both live in the same React
 *     subtree (the unauthenticated media viewer / SidekickView), so a
 *     module-level flag is sufficient and doesn't need cross-tree wiring.
 *
 * The flag is web-only by intent — native React Native platforms route
 * gestures through `react-native-gesture-handler` and don't suffer the
 * synthetic-click-bleed problem this guard solves.
 *
 * SAFETY: a hard upper bound (`MAX_GESTURE_DURATION_MS`) auto-resets the flag
 * even if `endAvatarGesture()` is never called. This guarantees the flag can
 * never get permanently stuck — which previously caused tap-capture
 * Pressables (e.g. tap-to-play/pause) to silently no-op after a sequence of
 * play/pause toggles. A stuck flag could happen if, for example, an inner
 * GestureDetector consumed a pointerup before our handler could observe it,
 * or if React re-mounted the handler element mid-gesture.
 */

/**
 * Set this to `true` to print verbose `[avatar-guard]` traces to the console.
 * Pair with `DEBUG_AVATAR_DRAG` in `useWebAvatarDrag.ts` and the
 * `DEBUG_AVATAR_GESTURE` constant in `AvatarOverlay.tsx` for full coverage.
 */
const DEBUG_AVATAR_GUARD = true

let isActive = false
let endTimer: ReturnType<typeof setTimeout> | null = null
let safetyTimer: ReturnType<typeof setTimeout> | null = null

/**
 * Maximum duration the gesture flag can stay active without an explicit
 * `endAvatarGesture()` call. A normal tap is < 300 ms; a drag/throw is at
 * most a second or two. 1500 ms is safely longer than any realistic gesture
 * but short enough that a stuck flag self-heals before the user notices.
 */
const MAX_GESTURE_DURATION_MS = 1500

function clearTimers() {
  if (endTimer) {
    clearTimeout(endTimer)
    endTimer = null
  }
  if (safetyTimer) {
    clearTimeout(safetyTimer)
    safetyTimer = null
  }
}

/**
 * Mark the start of an avatar gesture. Subsequent calls to
 * `isAvatarGestureActive()` return `true` until `endAvatarGesture()` is
 * called and its grace window elapses, OR until the safety ceiling fires —
 * whichever comes first.
 */
export function startAvatarGesture(): void {
  if (DEBUG_AVATAR_GUARD) {
    // eslint-disable-next-line no-console
    console.log("[avatar-guard] start", { wasActive: isActive })
  }
  isActive = true
  clearTimers()

  // Hard ceiling: the flag MUST clear within this window even if
  // `endAvatarGesture()` is never called for some reason.
  safetyTimer = setTimeout(() => {
    if (DEBUG_AVATAR_GUARD) {
      // eslint-disable-next-line no-console
      console.warn("[avatar-guard] safety-cleared (gesture never ended)")
    }
    isActive = false
    safetyTimer = null
    if (endTimer) {
      clearTimeout(endTimer)
      endTimer = null
    }
  }, MAX_GESTURE_DURATION_MS)
}

/**
 * Mark the end of an avatar gesture. The flag stays active for `graceMs`
 * after this call, which gives downstream `click` / `mouseup` events that
 * follow the gesture release a chance to be filtered out by the guard
 * (browsers dispatch the synthetic click *after* `pointerup`).
 */
export function endAvatarGesture(graceMs: number = 300): void {
  if (DEBUG_AVATAR_GUARD) {
    // eslint-disable-next-line no-console
    console.log("[avatar-guard] end (scheduled)", { graceMs, isActive })
  }
  if (endTimer) clearTimeout(endTimer)
  endTimer = setTimeout(() => {
    if (DEBUG_AVATAR_GUARD) {
      // eslint-disable-next-line no-console
      console.log("[avatar-guard] end (cleared)")
    }
    isActive = false
    endTimer = null
    if (safetyTimer) {
      clearTimeout(safetyTimer)
      safetyTimer = null
    }
  }, graceMs)
}

/**
 * Returns `true` if an avatar gesture is currently in progress, or finished
 * within the most recent grace window. Tap-capture handlers (e.g. the
 * `MediaPlayerShell` Pressable's `onPress`) should bail out when this is
 * `true` to avoid stealing taps that were aimed at the avatar.
 */
export function isAvatarGestureActive(): boolean {
  return isActive
}
